import { ErrorHandler, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../../auth/auth/auth.service';
import { environment } from '../../../environments/environment';
import { BusinessTag } from '../../business/models/business-tag.model';
import { Tag } from '../models/tag.model';

@Injectable()
export class TagService {

  constructor(private _http: HttpClient,
              private _auth: AuthService,
              private _errorHandler: ErrorHandler) {
  }

  /**
   * function makes get call to server api. gets all tags.
   * @return {Promise<BusinessTag[]>}
   */
  async getAllTags(): Promise<BusinessTag[]> {
    const URL = `${environment.airportsApiUrl}/tags`;
    try {
      const RESPONSE: any = await this._http.get(URL).toPromise();

      if (!RESPONSE) {
        this._errorHandler.handleError(new Error('API did not return valid tag object'));
      } else {
        return (RESPONSE || []).map(d => BusinessTag.parse(d));
      }
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * @param {number} tagID id of tag to add;
   * @param {number} businessID of business to add tag to;
   * function makes post call to api server. adds tag to business.
   * @return {Promise<BusinessTag[]>}
   */
  postTag(tagID: number, businessID: number): Promise<any> {
    const URL = `${environment.airportsApiUrl}/business/${businessID}/tag`;
    try {
      const BODY = {tagID: tagID};
      return this._http.post(URL, BODY).toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * @param {number} tagID id of tag to add;
   * @param {number} airportID of airport to add tag to;
   * function makes post call to api server.
   * @return {Promise<any>}
   */
  postAirportTag(tagID: number, airportID: number): Promise<any> {
    const URL = `${environment.airportsApiUrl}/airports/${airportID}/tag`;
    try {
      const BODY = {tagID: tagID};
      return this._http.post(URL, BODY).toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * updates tag in api server.
   * @param {number} tagID
   * @param {number} businessID
   * @return {Promise<any>}
   */
  putTag(tagID: number, businessID: number): Promise<any> {
    const URL = `${environment.airportsApiUrl}/business/${businessID}/tag/${tagID}`;
    try {
      return this._http.put(URL, '').toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   *
   * @param {number} tagID id of tag to delete;
   * @param {number} businessID of business to delete tag from;
   * function makes delete call to api server. deletes tag from api server.
   * @return {Promise<BusinessTag[]>}
   */
  deleteTag(tagID: number, businessID: number): Promise<any> {
    const URL = `${environment.airportsApiUrl}/business/${businessID}/tag/${tagID}`;
    try {
      return this._http.delete(URL).toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   *
   * @param {number} tagID id of tag to delete;
   * @param {number} airportID of airport to delete tag from;
   * function makes delete call to api server. deletes tag from api server.
   * @return {Promise<any>}
   */
  deleteAirportTag(tagID: number, airportID: number): Promise<any> {
    const URL = `${environment.airportsApiUrl}/airports/${airportID}/tag/${tagID}`;
    try {
      return this._http.delete(URL).toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * returns tag of given name
   * @param {Tag[]} tags
   * @param {string} name
   * @return {Tag}
   */
  findTagByName (tags: Tag[], name: string): Tag {
    return tags.find(tag => tag.tagName === name);
  }

  /**
   *
   * @param {Tag[]} tags
   * @param {number} id
   * @return {Tag}
   */
  findTagById (tags: Tag[], id: number): Tag {
    return tags.find(tag => tag.tagId === id);
  }

  /**
   * Seraches the given input list of tags for any that have the search value in the name and returns them
   * @param {Tag[]} tags
   * @param {string} name
   * @return {Tag[]}
   */
  searchTagsByName(tags: Tag[], searchValue: string): Tag[] {
    return tags.filter(tag => tag.tagName.includes(searchValue));
  }
}
